import * as React from 'react';
import { FaReact, FaPhp, FaHtml5, FaDatabase, FaWhatsapp, FaFolderOpen } from 'react-icons/fa6';

const Hero = () => {
  // Animation keyframes are now in the global CSS file

  
const starFieldStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    pointerEvents: 'none',
    overflow: 'hidden',
  };

  const createStars = (): React.ReactNode[] => {
    const stars: React.ReactNode[] = [];
    const starCount = 200; // Increased number of stars
    
    for (let i = 0; i < starCount; i++) {
      // Random position
      const left = Math.random() * 100;
      const top = Math.random() * 100;
      // Random size between 1px and 3px
      const size = 1 + Math.random() * 2;
      // Random opacity for depth
      const opacity = 0.3 + Math.random() * 0.7;
      // Random animation delay and duration
      const delay = Math.random() * 5;
      const duration = 2 + Math.random() * 3;
      
      stars.push(
        <div 
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            left: `${left}%`,
            top: `${top}%`,
            width: `${size}px`,
            height: `${size}px`,
            opacity: opacity,
            animation: `twinkle ${duration}s infinite ${delay}s`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      );
    }
    return stars;
  };

  const createBlackHoles = (): React.ReactNode[] => {
    const holes: React.ReactNode[] = [];
    const holeCount = 3; // Number of black holes
    
    for (let i = 0; i < holeCount; i++) {
      // Random position
      const left = 10 + Math.random() * 80;
      const top = 10 + Math.random() * 80;
      // Random size between 50px and 150px
      const size = 50 + Math.random() * 100;
      
      holes.push(
        <div 
          key={`hole-${i}`}
          className="absolute rounded-full"
          style={{
            left: `${left}%`,
            top: `${top}%`,
            width: `${size}px`,
            height: `${size}px`,
            background: 'radial-gradient(circle, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 70%)',
            transform: 'translate(-50%, -50%)',
            filter: 'blur(1px)',
          }}
        />
      );
    }
    return holes;
  };

  // État pour suivre l'icône actuellement affichée
  const [currentIconIndex, setCurrentIconIndex] = React.useState(0);

  // Désactive le défilement de la page et gère le changement d'icônes
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    // Change d'icône toutes les 2 secondes
    const interval = setInterval(() => {
      setCurrentIconIndex(prev => (prev + 1) % techIcons.length);
    }, 2000);
    
    return () => {
      document.body.style.overflow = 'auto';
      clearInterval(interval);
    };
  }, []);

  // Positions aléatoires pour les icônes de technologies
  const techIcons = [
    { icon: FaReact, name: 'React', color: '#61DAFB', position: { x: '65%', y: '70%' } },
    { icon: FaPhp, name: 'PHP', color: '#777BB4', position: { x: '80%', y: '25%' } },
    { icon: FaHtml5, name: 'HTML5', color: '#E34F26', position: { x: '35%', y: '65%' } },
    { icon: FaReact, name: 'React', color: '#61DAFB', position: { x: '65%', y: '70%' } },
    { icon: FaDatabase, name: 'SQL', color: '#336791', position: { x: '50%', y: '40%' } },
  ];

  return (
    <div className="min-h-screen w-full flex flex-col justify-center items-center text-center px-4 py-8 relative overflow-hidden">
      {/* Icônes de technologies */}
      {techIcons.map((tech, index) => (
        <div 
          key={index}
          className="absolute z-10 text-4xl opacity-0 tech-icon"
          style={{
            left: tech.position.x,
            top: tech.position.y,
            color: tech.color,
            transform: 'translate(-50%, -50%)',
            animation: `techPulse 4s infinite ${index * 0.5}s`,
            filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.5))',
          }}
        >
          <tech.icon />
          <span className="absolute text-xs font-mono mt-1 left-1/2 transform -translate-x-1/2">
            {tech.name}
          </span>
        </div>
      ))}

      <style>{`
        .hero-container {
          --color-0: #fff;
          --color-1: #111;
          --color-2: #222;
          --color-3: #333;
          --color-4: #2e2e2e;
          --color-5: #d2b48c;
          --color-6: #b22222;
          --color-7: #871a1a;
          --color-8: #ff6347;
          --color-9: #ff3814;
          width: 100vw;
          height: 100vh;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 0;
          background-color: var(--color-1);
          background-image: 
            linear-gradient(to top, var(--color-2) 5%, var(--color-1) 6%, var(--color-1) 7%, transparent 7%),
            linear-gradient(to bottom, var(--color-1) 30%, transparent 80%),
            linear-gradient(to right, var(--color-2), var(--color-4) 5%, transparent 5%),
            linear-gradient(to right, transparent 6%, var(--color-2) 6%, var(--color-4) 9%, transparent 9%),
            linear-gradient(to right, transparent 27%, var(--color-2) 27%, var(--color-4) 34%, transparent 34%),
            linear-gradient(to right, transparent 51%, var(--color-2) 51%, var(--color-4) 57%, transparent 57%),
            linear-gradient(to bottom, var(--color-1) 35%, transparent 35%),
            linear-gradient(to right, transparent 42%, var(--color-2) 42%, var(--color-4) 44%, transparent 44%),
            linear-gradient(to right, transparent 45%, var(--color-2) 45%, var(--color-4) 47%, transparent 47%),
            linear-gradient(to right, transparent 48%, var(--color-2) 48%, var(--color-4) 50%, transparent 50%),
            linear-gradient(to right, transparent 87%, var(--color-2) 87%, var(--color-4) 91%, transparent 91%),
            linear-gradient(to bottom, var(--color-1) 37.5%, transparent 37.5%),
            linear-gradient(to right, transparent 14%, var(--color-2) 14%, var(--color-4) 20%, transparent 20%),
            linear-gradient(to bottom, var(--color-1) 40%, transparent 40%),
            linear-gradient(to right, transparent 10%, var(--color-2) 10%, var(--color-4) 13%, transparent 13%),
            linear-gradient(to right, transparent 21%, var(--color-2) 21%, #1a1a1a 25%, transparent 25%),
            linear-gradient(to right, transparent 58%, var(--color-2) 58%, var(--color-4) 64%, transparent 64%),
            linear-gradient(to right, transparent 92%, var(--color-2) 92%, var(--color-4) 95%, transparent 95%),
            linear-gradient(to bottom, var(--color-1) 48%, transparent 48%),
            linear-gradient(to right, transparent 96%, var(--color-2) 96%, #1a1a1a 99%, transparent 99%),
            linear-gradient(to bottom, transparent 68.5%, transparent 76%, var(--color-1) 76%, var(--color-1) 77.5%, transparent 77.5%, transparent 86%, var(--color-1) 86%, var(--color-1) 87.5%, transparent 87.5%),
            linear-gradient(to right, transparent 35%, var(--color-2) 35%, var(--color-4) 41%, transparent 41%),
            linear-gradient(to bottom, var(--color-1) 68%, transparent 68%),
            linear-gradient(to right, transparent 78%, var(--color-3) 78%, var(--color-3) 80%, transparent 80%, transparent 82%, var(--color-3) 82%, var(--color-3) 83%, transparent 83%),
            linear-gradient(to right, transparent 66%, var(--color-2) 66%, var(--color-4) 85%, transparent 85%);
          background-size: 300px 150px;
          background-position: center bottom;
        }

        .hero-container:before {
          content: "";
          width: 100%;
          height: 100%;
          position: absolute;
          inset: 0;
          background-color: var(--color-1);
          background-image: 
            linear-gradient(to top, var(--color-5) 5%, var(--color-1) 6%, var(--color-1) 7%, transparent 7%),
            linear-gradient(to bottom, var(--color-1) 30%, transparent 30%),
            linear-gradient(to right, var(--color-6), var(--color-7) 5%, transparent 5%),
            linear-gradient(to right, transparent 6%, var(--color-8) 6%, var(--color-9) 9%, transparent 9%),
            linear-gradient(to right, transparent 27%, #556b2f 27%, #39481f 34%, transparent 34%),
            linear-gradient(to right, transparent 51%, #fa8072 51%, #f85441 57%, transparent 57%),
            linear-gradient(to bottom, var(--color-1) 35%, transparent 35%),
            linear-gradient(to right, transparent 42%, #008080 42%, #004d4d 44%, transparent 44%),
            linear-gradient(to right, transparent 45%, #008080 45%, #004d4d 47%, transparent 47%),
            linear-gradient(to right, transparent 48%, #008080 48%, #004d4d 50%, transparent 50%),
            linear-gradient(to right, transparent 87%, #789 87%, #4f5d6a 91%, transparent 91%),
            linear-gradient(to bottom, var(--color-1) 37.5%, transparent 37.5%),
            linear-gradient(to right, transparent 14%, #bdb76b 14%, #989244 20%, transparent 20%),
            linear-gradient(to bottom, var(--color-1) 40%, transparent 40%),
            linear-gradient(to right, transparent 10%, #808000 10%, #4d4d00 13%, transparent 13%),
            linear-gradient(to right, transparent 21%, #8b4513 21%, #5e2f0d 25%, transparent 25%),
            linear-gradient(to right, transparent 58%, #8b4513 58%, #5e2f0d 64%, transparent 64%),
            linear-gradient(to right, transparent 92%, #2f4f4f 92%, #1c2f2f 95%, transparent 95%),
            linear-gradient(to bottom, var(--color-1) 48%, transparent 48%),
            linear-gradient(to right, transparent 96%, #2f4f4f 96%, #1c2f2f 99%, transparent 99%),
            linear-gradient(to bottom, transparent 68.5%, transparent 76%, var(--color-1) 76%, var(--color-1) 77.5%, transparent 77.5%, transparent 86%, var(--color-1) 86%, var(--color-1) 87.5%, transparent 87.5%),
            linear-gradient(to right, transparent 35%, #cd5c5c 35%, #bc3a3a 41%, transparent 41%),
            linear-gradient(to bottom, var(--color-1) 68%, transparent 68%),
            linear-gradient(to right, transparent 78%, #bc8f8f 78%, #bc8f8f 80%, transparent 80%, transparent 82%, #bc8f8f 82%, #bc8f8f 83%, transparent 83%),
            linear-gradient(to right, transparent 66%, #a52a2a 66%, #7c2020 85%, transparent 85%);
          background-size: 300px 150px;
          background-position: center bottom;
          clip-path: circle(150px at center center);
          animation: flashlight 20s ease infinite;
        }

        /* Style pour le conteneur principal */

        @keyframes flashlight {
          0% { 
            clip-path: circle(100px at 20% 30%);
            opacity: 0.9;
          }
          15% { 
            clip-path: circle(100px at 80% 25%);
            opacity: 0.9;
          }
          30% { 
            clip-path: circle(100px at 35% 65%);
            opacity: 0.9;
          }
          45% { 
            clip-path: circle(100px at 65% 70%);
            opacity: 0.9;
          }
          60% { 
            clip-path: circle(100px at 50% 40%);
            opacity: 0.9;
          }
          75% { 
            clip-path: circle(100px at 80% 25%);
            opacity: 0.9;
          }
          90% { 
            clip-path: circle(100px at 35% 65%);
            opacity: 0.9;
          }
          100% { 
            clip-path: circle(100px at 65% 70%);
            opacity: 0.9;
          }
        }

        @keyframes techPulse {
          0%, 100% { 
            transform: translate(-50%, -50%) scale(1);
          }
          50% { 
            transform: translate(-50%, -50%) scale(1.1);
          }
        }

        @keyframes float {
          0% {
            transform: translate(-50%, 0) scale(0.8);
            opacity: 0;
            filter: blur(5px);
          }
          10% {
            opacity: 0.3;
            filter: blur(8px);
          }
          30% {
            transform: translate(-50%, -50px) scale(1.1);
            opacity: 0.4;
            filter: blur(12px);
          }
          50% {
            transform: translate(-50%, -120px) scale(1.3);
            opacity: 0.3;
            filter: blur(15px);
          }
          70% {
            transform: translate(-50%, -200px) scale(1.5);
            opacity: 0.2;
            filter: blur(18px);
          }
          90% {
            opacity: 0.1;
            filter: blur(20px);
          }
          100% {
            transform: translate(-50%, -300px) scale(1.8);
            opacity: 0;
            filter: blur(25px);
          }
        }
      `}</style>
      <div className="hero-container">
        {/* Star field with black holes */}
        <div style={starFieldStyle}>
          {createStars()}
          {createBlackHoles()}
        </div>
        
        {/* Icône de technologie active */}
        <div className="absolute inset-0 w-full h-full pointer-events-none z-10">
          {techIcons.map((tech, index) => (
            <div 
              key={index}
              className="absolute z-10 text-4xl tech-icon transition-all duration-1000"
              style={{
                left: tech.position.x,
                top: tech.position.y,
                color: tech.color,
                transform: `translate(-50%, -50%) scale(${index === currentIconIndex ? 1 : 0.5})`,
                filter: `drop-shadow(0 0 ${index === currentIconIndex ? '15px' : '0px'} ${tech.color}99)`,
                opacity: index === currentIconIndex ? 1 : 0,
                transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                pointerEvents: 'none'
              }}
            >
              <tech.icon className="transition-transform duration-300" />
              <span className="absolute text-xs font-mono mt-1 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                {tech.name}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="w-full max-w-6xl mx-auto px-4 sm:px-8 py-16 sm:py-24 relative z-50">
        <div className="flex flex-col lg:flex-row items-center">
          {/* Left Column - Text Content */}
          <div className="w-full lg:w-1/2 space-y-6 sm:space-y-7 text-center lg:text-left">
            <h1 className="text-6xl md:text-7xl font-bold text-white leading-tight text-center lg:text-left">
              <div className="text-3xl md:text-4xl text-gray-400 mb-2">Salut, je suis</div>
              <div className="bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">Rani</div>
              <div className="bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-green-600 -mt-3">Hamed</div>
            </h1>
            
            <div className="relative inline-flex items-center px-4 sm:px-6 py-1.5 sm:py-2.5 rounded-full bg-gradient-to-r from-gray-900 to-black text-green-300 font-medium text-xs sm:text-sm tracking-wide shadow-lg shadow-green-500/30 group hover:shadow-green-500/50 transition-all duration-300 overflow-hidden hover:scale-105 transform border border-green-500/40">
              <span className="relative z-10 flex items-center gap-2">
                <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-green-400 animate-pulse flex-shrink-0"></span>
                <span className="text-green-300 drop-shadow-[0_0_4px_rgba(74,222,128,0.7)] text-xs sm:text-sm whitespace-nowrap">
                  <span className="hidden sm:inline">Développeur Full Stack & Webmaster</span>
                  <span className="sm:hidden">Dev Full Stack / Webmaster</span>
                </span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-emerald-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute -inset-1 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full opacity-0 group-hover:opacity-30 blur-md transition-all duration-700"></div>
            </div>
            
            <p className="text-[1.05rem] text-gray-300 w-full max-w-[24rem] mx-auto lg:mx-0 leading-tight">
              Expert en développement web & mobile full-stack, je conçois et développe des solutions numériques sur mesure, alliant performance, design moderne et expérience utilisateur optimale.
            </p>
            
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-green-500/20 rounded-full filter blur-3xl"></div>
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-blue-500/20 rounded-full filter blur-3xl"></div>
            
            {/* Boutons - Visibles sur tous les appareils */}
            <div className="flex flex-row justify-center gap-4 mt-8 w-full lg:hidden">
              <a 
                href="https://wa.me/22673525432?text=Bonjour%20Rani,%20je%20suis%20intéressé(e)%20par%20vos%20compétences%20en%20développement%20web%20et%20mobile.%20Pouvons-nous%20en%20discuter%20?" 
                target="_blank"
                rel="noopener noreferrer"
                className="group relative w-40 px-4 py-2 rounded-full bg-gradient-to-r from-green-900/80 to-green-800/80 text-green-300 font-medium text-xs tracking-wide shadow-lg shadow-green-500/20 transition-all duration-300 overflow-hidden hover:scale-105 transform border border-green-500/30 flex items-center justify-center whitespace-nowrap"
              >
                <FaWhatsapp className="text-green-400 text-lg mr-2" />
                <span>Me Contacter</span>
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </a>
              
              <button 
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('navigateToSection', { detail: 'projects' }));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="group relative w-40 px-4 py-2 rounded-full bg-gradient-to-r from-gray-900/80 to-gray-800/80 text-gray-300 font-medium text-xs tracking-wide shadow-lg shadow-gray-500/10 border border-gray-600/30 transition-all duration-300 overflow-hidden hover:scale-105 transform flex items-center justify-center gap-2 cursor-pointer"
              >
                <FaFolderOpen className="text-gray-400 text-base mr-1" />
                <span>Mes Projets</span>
                <div className="absolute inset-0 bg-gradient-to-r from-gray-700/10 to-gray-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>
          </div>
          
          {/* Right Column - Image - Hidden on mobile */}
          <div className="hidden lg:flex lg:w-1/2 flex-col items-center lg:items-end mt-12 lg:mt-0 lg:mr-0 lg:ml-64 xl:ml-80 2xl:ml-96">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-blue-500/20 rounded-full filter blur-3xl -z-10 animate-pulse"></div>
              <img 
                src="/assets/dev.png" 
                alt="Développement web et mobile" 
                className="w-72 h-72 md:w-96 md:h-96 lg:w-[28rem] lg:h-[28rem] opacity-90 hover:opacity-100 transition-all duration-300 hover:scale-105 animate-float"
              />
            </div>
            
            {/* Boutons pour les écrans larges */}
            <div className="hidden lg:flex flex-row justify-center gap-4 mt-8 lg:mt-4 w-full">
              <a 
                href="https://wa.me/22673525432?text=Bonjour%20Rani,%20je%20suis%20intéressé(e)%20par%20vos%20compétences%20en%20développement%20web%20et%20mobile.%20Pouvons-nous%20en%20discuter%20?" 
                target="_blank"
                rel="noopener noreferrer"
                className="group relative w-40 px-4 py-2 rounded-full bg-gradient-to-r from-green-900/80 to-green-800/80 text-green-300 font-medium text-xs tracking-wide shadow-lg shadow-green-500/20 transition-all duration-300 overflow-hidden hover:scale-105 transform border border-green-500/30 flex items-center justify-center whitespace-nowrap"
              >
                <FaWhatsapp className="text-green-400 text-lg mr-2" />
                <span>Me Contacter</span>
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </a>
              
              <button 
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('navigateToSection', { detail: 'projects' }));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="group relative w-40 px-4 py-2 rounded-full bg-gradient-to-r from-gray-900/80 to-gray-800/80 text-gray-300 font-medium text-xs tracking-wide shadow-lg shadow-gray-500/10 border border-gray-600/30 transition-all duration-300 overflow-hidden hover:scale-105 transform flex items-center justify-center gap-2 cursor-pointer"
              >
                <FaFolderOpen className="text-gray-400 text-base mr-1" />
                <span>Mes Projets</span>
                <div className="absolute inset-0 bg-gradient-to-r from-gray-700/10 to-gray-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>
            <style>{`
              @keyframes float {
                0% { transform: translateY(0px); }
                50% { transform: translateY(-10px); }
                100% { transform: translateY(0px); }
              }
              .animate-float {
                animation: float 6s ease-in-out infinite;
              }
            `}</style>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Hero;
